import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Loader from '../Loader';
import { toast } from 'react-toastify';
import { 
  useDisconnect,
  useAppKit,
  useAppKitAccount,
} from '@reown/appkit/react';

function ClaimTokens() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [solanaAddress, setSolanaAddress] = useState('');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentClaimId, setCurrentClaimId] = useState(null);
  const [currentTxHash, setCurrentTxHash] = useState(null);
  const itemsPerPage = 10;
  const baseUrl = process.env.REACT_APP_API;

  // Wallet connection hooks
  const { disconnect } = useDisconnect();
  const { open } = useAppKit();
  const { isConnected, address } = useAppKitAccount() || {};

  const handleConnectWallet = async () => {
    try {
      setLoading(true);
      await open?.();
    } catch (error) {
      console.error('Wallet connection error:', error);
      toast.error('Failed to connect wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect?.();
    } catch (error) {
      console.error('Wallet disconnection error:', error);
      toast.error('Failed to disconnect wallet');
    }
  };

  const fetchTransactionHistory = async (walletAddress) => {
    if (!walletAddress) return;

    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/quick-payment/${walletAddress}`);
      setClaims(response.data.reverse());
    } catch (err) {
      if (err.response) {
        if (err.response.status === 404) {
          toast.info('No transactions found for this wallet address.');
          setClaims([]);
        } else {
          toast.error(`Server error: ${err.response.statusText || 'Unknown error'}`);
        }
      } else if (err.request) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  let isMounted = true;
  const timer = setTimeout(() => {
    if (isConnected && address) {
      if (!loading) { // Only fetch if not already loading
        fetchTransactionHistory(address);
      }
    } else {
      if (isMounted) setClaims([]);
    }
  }, 100); // Small debounce delay

  return () => {
    isMounted = false;
    clearTimeout(timer);
  };
}, [isConnected, address]);

  const isValidSolanaAddress = (address) => {
    return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
  };

  const initiateClaim = (id, txHash) => {
    setCurrentClaimId(id);
    setCurrentTxHash(txHash);
    setShowAddressModal(true);
    setSolanaAddress('');
  };

  const proceedToConfirmation = () => {
    if (!isValidSolanaAddress(solanaAddress)) {
      toast.error('Please enter a valid Solana address');
      return;
    }
    setShowAddressModal(false);
    setShowConfirmationModal(true);
  };

  const handleClaimTokens = async () => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${baseUrl}/api/quick-payment/claim`, {
        claimId: currentClaimId,
        solanaAddress: solanaAddress,
        txHash: currentTxHash
      });

      // Update the specific claim in the claims array
      setClaims(prevClaims => 
        prevClaims.map(claim => 
          claim.id === currentClaimId ? { 
            ...claim, 
            rzto_transferred: true, 
            status: 'completed',
            txid_out: response.data.txSignature,
            updated_at: new Date().toISOString()
          } : claim
        )
      );
      
      toast.success(
  <div>
    Tokens claimed successfully!
    <br />
    <a 
      href={getExplorerUrl(response.data.txSignature, 'sol')} 
      target="_blank" 
      rel="noopener noreferrer"
      style={{ color: 'white', textDecoration: 'underline' }}
    >
      View transaction
    </a>
  </div>
);
    } catch (err) {
      console.error('Error claiming tokens:', err);
      toast.error(err.response?.data?.error || 'Failed to claim tokens');
    } finally {
      setLoading(false);
      setShowConfirmationModal(false);
    }
  };

  // Pagination logic
  const indexOfLastTransaction = currentPage * itemsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - itemsPerPage;
  const currentClaims = claims.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const getSerialNumber = (index) => index + indexOfFirstTransaction + 1;

  if (loading && !showAddressModal && !showConfirmationModal) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Loader />
      </div>
    );
  }


  const getExplorerUrl = (txHash, chain) => {
  if (chain === 'SOL') {
    return `https://solscan.io/tx/${txHash}`;
  } else {
    return `https://etherscan.io/tx/${txHash}`;
  }
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      {/* Solana Address Input Modal */}
      {showAddressModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '25px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>Enter Solana Address</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Please enter your Solana wallet address to receive RZTO tokens
            </p>
            <input
              type="text"
              value={solanaAddress}
              onChange={(e) => setSolanaAddress(e.target.value)}
              placeholder="Enter Solana wallet address"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                marginBottom: '15px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={() => setShowAddressModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={proceedToConfirmation}
                disabled={!solanaAddress}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  opacity: !solanaAddress ? 0.7 : 1
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '25px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>Confirm Token Claim</h3>
            <p style={{ color: '#666', marginBottom: '10px' }}>
              You are about to claim tokens to:
            </p>
            <p style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
              wordBreak: 'break-all',
              marginBottom: '20px'
            }}>
              <strong>{solanaAddress}</strong>
            </p>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Please confirm this is the correct Solana address to receive your RZTO tokens.
              Providing a wrong Solana address may result in loss of RZTO tokens.
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={() => {
                  setShowConfirmationModal(false);
                  setShowAddressModal(true);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Back
              </button>
              <button
                onClick={handleClaimTokens}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Processing...' : 'Confirm Claim'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        padding: '25px'
      }}>
        <h6 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '20px',
          color: '#333'
        }}><strong>Token Claim History</strong></h6>

        {!isConnected ? (
          <div style={{
            textAlign: 'center',
            padding: '30px',
            border: '1px dashed #ddd',
            borderRadius: '8px'
          }}>
            <p style={{ marginBottom: '20px', color: '#666' }}>Connect your Ethereum wallet to view and claim tokens</p>
            <button 
              onClick={handleConnectWallet}
              disabled={loading}
              style={{
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                opacity: loading ? '0.7' : '1'
              }}
            >
              {loading ? 'Connecting...' : 'Connect ETH Wallet'}
            </button>
          </div>
        ) : (
          <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '6px'
            }}>
              <p style={{ margin: '0', color: '#555', wordBreak: 'break-all' }}>
                Connected Wallet: <strong style={{ color: '#333' }}>{address}</strong>
              </p>
              <button 
                onClick={handleDisconnect} 
                disabled={loading}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  alignSelf: 'flex-end'
                }}
              >
                Disconnect
              </button>
            </div>

            {claims.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '30px',
                border: '1px dashed #ddd',
                borderRadius: '8px',
                color: '#666'
              }}>
                <p>No Claimable Transactions Found</p>
              </div>
            ) : (
              <>
                <div style={{
                  overflowX: 'auto',
                  marginBottom: '20px',
                  WebkitOverflowScrolling: 'touch'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    minWidth: '800px'
                  }}>
                    <thead>
                      <tr style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '2px solid #dee2e6'
                      }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>#</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Currency</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Amount</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>USD</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>RZTO</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Time</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Action</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Transaction</th>
                        {/* <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Solana TX</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {currentClaims.map((claim, index) => (
                        <tr key={claim.id} style={{
                          borderBottom: '1px solid #eee',
                          ':hover': {
                            backgroundColor: '#f9f9f9'
                          }
                        }}>
                          <td style={{ padding: '12px' }}>{getSerialNumber(index)}</td>
                          <td style={{ padding: '12px' }}>{claim.currency?.toUpperCase()}</td>
                          <td style={{ padding: '12px' }}>{claim.currency_amount}</td>
                          <td style={{ padding: '12px' }}>${claim.usd_amount.toFixed(2)}</td>
                          <td style={{ padding: '12px' }}>{claim.rzto_amount.toFixed(2)}</td>
                          <td style={{ padding: '12px' }}>
                            {claim.status === 'completed' ? (
                              <span style={{
                                backgroundColor: '#28a745',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>Completed</span>
                            ) : (
                              <span style={{
                                backgroundColor: '#ffc107',
                                color: '#212529',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>{claim.status}</span>
                            )}
                          </td>
                          <td style={{ padding: '12px' }}>{new Date(claim.created_at).toLocaleString()}</td>
                          <td style={{ padding: '12px' }}>
                            {!claim.rzto_transferred && claim.rzto_amount > 0 ? (
                              <button 
                                onClick={() => initiateClaim(claim.id, claim.txid_in)}
                                disabled={loading}
                                style={{
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  opacity: loading ? '0.7' : '1'
                                }}
                              >
                                {loading ? 'Processing...' : 'Claim Token'}
                              </button>
                            ) : (
                              <span style={{
                                color: claim.rzto_transferred ? '#28a745' : '#6c757d',
                                fontWeight: claim.rzto_transferred ? '500' : '400'
                              }}>
                                {claim.rzto_transferred ? 'Token Claimed' : 'Not Claimable'}
                              </span>
                            )}
                          </td>
                         <td style={{ padding: '12px' }}>
                          {claim.txid_in && (
                            <a 
                              href={getExplorerUrl(claim.txid_in, claim.currency?.toUpperCase())} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{
                                color: '#007bff',
                                textDecoration: 'none',
                                ':hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              View on {claim.currency?.toUpperCase() === 'SOL' ? 'Solana' : 'Ethereum'}
                            </a>
                          )}
                        </td>
                          {/* <td style={{ padding: '12px' }}>
                            {claim.txid_out ? (
                              <a 
                                href={`https://solscan.io/tx/${claim.txid_out}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{
                                  color: '#007bff',
                                  textDecoration: 'none',
                                  ':hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                View on Solana
                              </a>
                            ) : claim.rzto_transferred ? (
                              <span style={{ color: '#6c757d' }}>Processing...</span>
                            ) : null}
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {claims.length > itemsPerPage && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    marginTop: '20px'
                  }}>
                    <button 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 15px',
                        border: '1px solid #ddd',
                        backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                        color: currentPage === 1 ? '#aaa' : '#333',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        minWidth: '100px'
                      }}
                    >
                      Previous
                    </button>
                    <span style={{
                      padding: '8px 15px',
                      color: '#666'
                    }}>
                      Page {currentPage} of {Math.ceil(claims.length / itemsPerPage)}
                    </span>
                    <button 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(claims.length / itemsPerPage)))}
                      disabled={currentPage === Math.ceil(claims.length / itemsPerPage)}
                      style={{
                        padding: '8px 15px',
                        border: '1px solid #ddd',
                        backgroundColor: currentPage === Math.ceil(claims.length / itemsPerPage) ? '#f5f5f5' : '#fff',
                        color: currentPage === Math.ceil(claims.length / itemsPerPage) ? '#aaa' : '#333',
                        cursor: currentPage === Math.ceil(claims.length / itemsPerPage) ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        minWidth: '100px'
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ClaimTokens;